import toast from "react-hot-toast";
import * as minutesPackageApi from "../../api/minutesPackage";
import * as flexibleDiscountApi from "../../api/flexibleDiscount";
import { TabList, Tab } from "../../TabList";
import CustomerInfo from "../../components/CustomerInfo";
import CheckoutMinutes from "../../components/CheckoutMinutes";
import BuyMinutesForm from "../../components/BuyMinutes";
import FlexibleDiscount from "../../components/FlexibleDiscount";
import Table from "../../components/Table";
import { TRANSACTION_REASON_TO_TEXT } from "../../domain/transaction";
import { useEffect, useState } from "react";
import { formatObject } from "../../common/formatting";
import { useDispatch, useSelector } from "react-redux";
import {
  updateBalance,
  selectCurrent,
  changeCurrent,
} from "../../store/customer";
import { selectAccessToken } from "../../store/auth";
import HistoryContextMenu from "../../components/HistoryContextMenu";
import * as customerApi from "../../api/customer";
import { useTranslation } from "../../i18n";

/**
 * @returns {React.Component}
 */
function RecentCustomer({ setCanBecomeVip }) {
  const { t, locale } = useTranslation();
  const dispatch = useDispatch();
  /** @type {RegisteredCustomer} */
  const customer = useSelector(selectCurrent);
  const accessToken = useSelector(selectAccessToken);

  const [packages, setPackages] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  /** @type {ReturnType<typeof useState<number>>} */
  const [activeFlexDiscountIdx, setActiveFlexDiscountIdx] = useState(null);
  const [controlledActiveTab, setControlledActiveTab] = useState("buyMinutes");

  useEffect(() => {
    if (packages.length <= 0) return;
    if (packages.find((p) => p.promotesToVip)) {
      setCanBecomeVip(true);
    }
  }, [packages, setCanBecomeVip]);

  useEffect(() => {
    if (
      controlledActiveTab === "history" &&
      customer != null &&
      accessToken != null
    ) {
      customerApi
        .fetchTransactions(accessToken, customer.id)
        .then((transactions) => {
          const updatedCustomer = { ...customer, transactions };
          dispatch(changeCurrent(updatedCustomer));
        })
        .catch((err) => {
          console.error("Failed to fetch transactions in background:", err);
        });
    }
  }, [controlledActiveTab, customer?.id, accessToken]);

  const refreshMinutesPackages = () => {
    console.debug("Fetching customers packages");
    if (customer == null) {
      setPackages([]);
      return;
    }
    minutesPackageApi
      .fetchForCustomer(accessToken, customer.id)
      .then((resp) => {
        console.debug("Updating packages", resp);
        setPackages(resp.packages);
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("toast.packagesLoadError"));
      });
  };

  useEffect(() => {
    refreshMinutesPackages();
  }, [customer?.minutesBalance]);

  useEffect(() => {
    if (accessToken == null) return;
    const inner = async () => {
      setDiscounts(
        await flexibleDiscountApi.fetchFlexibleDiscounts(accessToken),
      );
    };
    inner();
  }, [accessToken]);

  return (
    customer && (
      <div className="">
        <CustomerInfo customer={customer} />
        <TabList
          className="bg-c-gray-600 mt-4 max-h-[calc(100vh-16em)] overflow-hidden"
          controlledActiveTab={controlledActiveTab}
          setControlledActiveTab={setControlledActiveTab}
        >
          <Tab label={t("customerTab.buyMinutes")} name="buyMinutes">
            <BuyMinutesForm
              minutesPackages={packages}
              refreshMinutesPackages={refreshMinutesPackages}
              flexibleDiscount={discounts[activeFlexDiscountIdx]}
            />
          </Tab>
          <Tab label={t("customerTab.checkoutMinutes")} name="checkoutMinutes">
            <CheckoutMinutes
              customer={customer}
              setCustomerBalance={(balance) => dispatch(updateBalance(balance))}
              switchToBuyMinutes={() => {
                setControlledActiveTab("buyMinutes");
              }}
            />
          </Tab>
          <Tab label={t("customerTab.history")} name="history">
            <Table
              ContextMenu={({ children, row }) => (
                <HistoryContextMenu
                  children={children}
                  row={row}
                  onTransactionCancelled={async () => {
                    // Refresh customer data after transaction cancellation
                    try {
                      const updatedCustomer =
                        await customerApi.findCustomerById(
                          accessToken,
                          customer.id,
                        );
                      if (updatedCustomer !== 404) {
                        dispatch(changeCurrent(updatedCustomer));
                      }
                    } catch (error) {
                      console.error("Failed to refresh customer data:", error);
                      toast.error(t("toast.failedToUpdateCustomer"));
                    }
                  }}
                />
              )}
              head={[
                {
                  title: t("history.date"),
                  extractor: "createdAt",
                  type: "datetime",
                },
                {
                  title: t("history.employee"),
                  extractor: "user",
                  type: "custom",
                  /**
                   * @param {User} user
                   * @returns {string}
                   */
                  mapper: (user) => `${user.firstname} ${user.lastname}`,
                },
                {
                  title: t("history.location"),
                  extractor: "location.name",
                  type: "text",
                },
                {
                  title: t("history.minutes"),
                  extractor: "minutesAmount",
                  type: "number",
                },
                {
                  title: t("history.amount"),
                  extractor: "priceRub",
                  type: "custom",
                  mapper: (price) =>
                    price == null ? "-" : formatObject(price, "priceRub"),
                },
                {
                  title: t("history.service"),
                  extractor: "serviceName",
                  type: "custom",
                  /**
                   * @param {string|null} serviceName
                   * @returns {string}
                   */
                  mapper: (serviceName) => {
                    return serviceName || "-";
                  },
                },
                {
                  title: t("history.event"),
                  extractor: "reason",
                  type: "custom",
                  /**
                   * @param {string} txReason
                   * @returns {string}
                   */
                  mapper: (txReason) => {
                    const dict =
                      TRANSACTION_REASON_TO_TEXT[locale] ??
                      TRANSACTION_REASON_TO_TEXT["ru"];
                    const text = dict[txReason];
                    if (text == null) {
                      console.error(
                        "Got unknown transaction reason:",
                        txReason,
                      );
                    }
                    return text;
                  },
                },
              ]}
              rows={customer.transactions}
              initialSortDirection="descending"
            />
          </Tab>
          <Tab label={t("customerTab.discounts")} name="discounts">
            <FlexibleDiscount
              discounts={discounts}
              activeIdx={activeFlexDiscountIdx}
              setActiveIdx={setActiveFlexDiscountIdx}
              hasAutoDiscount={customer.debt > 0}
            />
          </Tab>
        </TabList>
      </div>
    )
  );
}

export default RecentCustomer;
